import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { taskAPI, projectAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiCheckSquare, FiX, FiSearch } from 'react-icons/fi';
import { useConfirm } from '../components/ConfirmModal';

const PCOL = { low:'#22c55e', medium:'#f59e0b', high:'#ef4444', critical:'#7c3aed' };
const SCOL = { todo:{bg:'#f1f5f9',txt:'#475569'}, 'in-progress':{bg:'#fef3c7',txt:'#92400e'}, review:{bg:'#e0e7ff',txt:'#3730a3'}, completed:{bg:'#d1fae5',txt:'#065f46'} };

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';
  const isClient = user?.role === 'client';
  const canAssign = !isClient; // only admin & team can assign; clients just pick a project
  const confirm = useConfirm();
  const [tasks,setTasks]=useState([]); const [projects,setProjects]=useState([]); const [users,setUsers]=useState([]);
  const [filter,setFilter]=useState('all'); const [search,setSearch]=useState('');
  const [view,setView]=useState('list'); // list or kanban
  const [modal,setModal]=useState(false); const [loading,setLoading]=useState(true);
  const [form,setForm]=useState({ title:'',description:'',project:'',assignedTo:'',priority:'medium',dueDate:'',estimatedHours:'',recurrence:{ enabled:false, frequency:'weekly' } });
  const [editModal,setEditModal]=useState(false); const [editId,setEditId]=useState(null);
  const [editForm,setEditForm]=useState({ title:'',description:'',priority:'medium',dueDate:'',estimatedHours:'',status:'todo',assignedTo:'' });
  const [selected,setSelected]=useState(new Set());
  const [deadlines,setDeadlines]=useState([]);
  const [dragId,setDragId]=useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  useEffect(()=>{ load(); loadDeadlines(); },[filter]); // eslint-disable-line react-hooks/exhaustive-deps
  // Reset to page 1 whenever filter or search changes
  useEffect(()=>{ setPage(1); },[filter, search]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const [tR,pR,uR] = await Promise.all([taskAPI.getAll(params), projectAPI.getAll(), canAssign?userAPI.getAll():Promise.resolve({data:{users:[]}})]);
      setTasks(tR.data.tasks||[]); setProjects(pR.data.projects||[]); setUsers(uR.data.users||[]);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const loadDeadlines = async () => {
    try { const r = await projectAPI.getDeadlineAlerts(7); setDeadlines(r.data.data||[]); } catch {}
  };
  const toggleSelect = (id) => { const s=new Set(selected); s.has(id)?s.delete(id):s.add(id); setSelected(s); };
  const toggleSelectAll = (filteredTasks) => { if(selected.size===filteredTasks.length) setSelected(new Set()); else setSelected(new Set(filteredTasks.map(t=>t._id))); }; // eslint-disable-line
  const bulkStatusUpdate = async (status) => {
    if(!selected.size) return toast.error('Select tasks first');
    try { await taskAPI.bulkUpdate({taskIds:[...selected],update:{status}}); toast.success(`${selected.size} tasks updated`); setSelected(new Set()); load(); } catch { toast.error('Bulk update failed'); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    const ok = await confirm(`Delete ${selected.size} task(s)?`, { title: 'Delete Tasks?', variant: 'danger' });
    if (!ok) return;
    try { for(const id of [...selected]) await taskAPI.delete(id); toast.success(`${selected.size} deleted`); setSelected(new Set()); load(); } catch { toast.error('Failed'); }
  };
  const createTask = async () => {
    if(!form.title.trim()) return toast.error('Title required');
    if(!form.dueDate) return toast.error('Due date is required');
    try { await taskAPI.create(form); toast.success('Task created'); setModal(false); setForm({title:'',description:'',project:'',assignedTo:'',priority:'medium',dueDate:'',estimatedHours:''}); load(); }
    catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const updateStatus = async (id, status) => { try { await taskAPI.update(id,{status}); load(); } catch (e) { toast.error(e.response?.data?.message || 'Update failed'); } };

  const openEdit = (t, e) => {
    e.preventDefault(); e.stopPropagation();
    const assignee = Array.isArray(t.assignedTo) ? (t.assignedTo[0]?.id || t.assignedTo[0]?._id || '') : (t.assignedTo?.id || '');
    setEditId(t._id || t.id);
    setEditForm({ title:t.title||'', description:t.description||'', priority:t.priority||'medium', dueDate:t.dueDate?t.dueDate.split('T')[0]:'', estimatedHours:t.estimatedHours||'', status:t.status||'todo', assignedTo: String(assignee) });
    setEditModal(true);
  };
  const saveEdit = async () => {
    if(!editForm.title.trim()) return toast.error('Title required');
    try { await taskAPI.update(editId, editForm); toast.success('Task updated'); setEditModal(false); load(); }
    catch(e) { toast.error(e.response?.data?.message||'Failed to update'); }
  };

  // Drag-and-drop handlers
  const onDragStart = (e, taskId) => { setDragId(taskId); e.dataTransfer.effectAllowed='move'; };
  const onDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect='move'; };
  const onDrop      = async (e, col) => {
    e.preventDefault();
    if (!dragId) return;
    const task = tasks.find(t => t._id === dragId);
    if (task && task.status !== col) await updateStatus(dragId, col);
    setDragId(null);
  };

  // Project-filtered members for "Assign To"
  const selectedProject = projects.find(p => String(p._id) === String(form.project));
  const assignableUsers = form.project && selectedProject?.teamMembers?.length
    ? selectedProject.teamMembers
    : users.filter(u => u.role !== 'client');

  const filtered = tasks.filter(t => {
    const q = search.toLowerCase();
    if (!q) return true;
    const titleMatch   = (t.title||'').toLowerCase().includes(q);
    const projectMatch = (t.project?.name||'').toLowerCase().includes(q);
    const assigneeMatch = Array.isArray(t.assignedTo)
      ? t.assignedTo.some(a=>(a.name||'').toLowerCase().includes(q))
      : (t.assignedTo?.name||'').toLowerCase().includes(q);
    return titleMatch || projectMatch || assigneeMatch;
  });

  const KANBAN_COLS = ['todo','in-progress','review','completed'];

  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}><div style={{width:40,height:40,border:'3px solid #e2e8f0',borderTop:'3px solid #6366f1',borderRadius:'50%',animation:'spin 1s linear infinite'}}/></div>;

  const S = {
    page:{maxWidth:1280,margin:'0 auto',fontFamily:"'Sora',sans-serif"},
    btn:(v,sm)=>({padding:sm?'7px 14px':'10px 20px',borderRadius:10,border:'none',fontWeight:600,fontSize:sm?12:14,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6,transition:'all .2s',
      ...(v==='primary'?{background:'var(--pm-gradient)',color:'#fff'}:v==='ghost-active'?{background:'var(--pm-primary)',color:'#fff',border:'none'}:{background:'#f8fafc',color:'#64748b',border:'1.5px solid #e2e8f0'})}),
    inp:{width:'100%',padding:'9px 13px',border:'2px solid #e2e8f0',borderRadius:9,fontSize:14,outline:'none',boxSizing:'border-box',marginTop:4},
    overlay:{position:'fixed',inset:0,background:'#00000060',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20},
    modal:{background:'#fff',borderRadius:20,padding:32,width:'100%',maxWidth:540,maxHeight:'90vh',overflowY:'auto'},
    label:{display:'block',fontSize:13,fontWeight:600,color:'#475569',marginBottom:14},
  };

  const TaskCard = ({ t }) => {
    const sc = SCOL[t.status]||SCOL.todo;
    const isSel = selected.has(t._id);
    return (
      <div style={{background:isSel?'#eff6ff':'#fff',borderRadius:14,padding:16,boxShadow:'0 1px 6px #0000000a',border:`1.5px solid ${isSel?'var(--pm-primary)':'#f1f5f9'}`,marginBottom:10,transition:'all .2s',cursor:'pointer',position:'relative'}}
        onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 16px #0000001a'}
        onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 6px #0000000a'}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
          <span style={{background:PCOL[t.priority]+'18',color:PCOL[t.priority],padding:'3px 10px',borderRadius:10,fontSize:11,fontWeight:700,textTransform:'uppercase'}}>{t.priority}</span>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {view==='list'&&<span style={{background:sc.bg,color:sc.txt,padding:'3px 10px',borderRadius:10,fontSize:11,fontWeight:700,textTransform:'capitalize'}}>{t.status}</span>}
            <button onClick={e=>openEdit(t,e)} title="Edit task" style={{background:'#f1f5f9',border:'none',borderRadius:6,padding:'3px 7px',cursor:'pointer',fontSize:12,color:'#475569',fontWeight:600,display:'flex',alignItems:'center',gap:3}}>✏️</button>
            <div onClick={e=>{e.preventDefault();e.stopPropagation();toggleSelect(t._id);}} style={{width:18,height:18,border:`2px solid ${isSel?'var(--pm-primary)':'#e2e8f0'}`,borderRadius:4,background:isSel?'var(--pm-primary)':'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',flexShrink:0,transition:'all .15s'}}>
              {isSel&&<span style={{color:'#fff',fontSize:10,fontWeight:800}}>✓</span>}
            </div>
          </div>
        </div>
        <Link to={`/tasks/${t._id}`} style={{textDecoration:'none'}}><div style={{fontWeight:600,fontSize:14,color:'#0f172a',marginBottom:6,lineHeight:1.3}}>{t.title}</div></Link>
        {t.description&&<div style={{fontSize:12,color:'#94a3b8',marginBottom:10,lineHeight:1.4,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{t.description}</div>}
        {/* Checklist progress */}
        {Array.isArray(t.checklist) && t.checklist.length > 0 && (() => {
          const total = t.checklist.length;
          const done  = t.checklist.filter(c => c.completed).length;
          const pct   = Math.round((done / total) * 100);
          return (
            <div style={{marginBottom:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <span style={{fontSize:11,color: done===total?'#10b981':'#64748b',fontWeight:600}}>
                  ☑ {done}/{total} checklist
                </span>
                <span style={{fontSize:10,color:'#94a3b8'}}>{pct}%</span>
              </div>
              <div style={{height:3,background:'#f1f5f9',borderRadius:2,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${pct}%`,background: done===total?'#10b981':'var(--pm-primary)',borderRadius:2,transition:'width .4s'}}/>
              </div>
            </div>
          );
        })()}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:11,color:'#94a3b8'}}>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            <span style={{fontWeight:500}}>{t.project?.name}</span>
            {t.assignedBy&&<span style={{fontSize:10,color:'#94a3b8'}}>👤 by {t.assignedBy.name}</span>}
          </div>
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            {t.recurrence?.enabled&&<span title={`Repeats ${t.recurrence.frequency}`}>🔁</span>}
            {t.dueDate&&<span>📅 {new Date(t.dueDate).toLocaleDateString()}</span>}
            {Array.isArray(t.assignedTo) ? t.assignedTo.slice(0,3).map((a,i)=>(
              <div key={i} title={a.name} style={{width:22,height:22,borderRadius:'50%',background:'var(--pm-primary)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,marginLeft:i>0?-6:0,border:'2px solid #fff',zIndex:3-i}}>{(a.name||'?').charAt(0)}</div>
            )) : t.assignedTo&&<div title={t.assignedTo.name} style={{width:22,height:22,borderRadius:'50%',background:'var(--pm-primary)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700}}>{t.assignedTo.name?.charAt(0)}</div>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {deadlines.length>0&&(
        <div style={{background:'linear-gradient(135deg,#fef3c7,#fffbeb)',border:'2px solid #fcd34d',borderRadius:14,padding:'14px 20px',marginBottom:20,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <span style={{fontSize:18}}>⚠️</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,color:'#92400e',fontSize:14}}>Deadline Alert — {deadlines.length} project{deadlines.length!==1?'s':''} due within 7 days</div>
            <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:6}}>
              {deadlines.map(p=><a key={p._id} href={'/projects/'+p._id} style={{fontSize:12,fontWeight:600,color:'#b45309',textDecoration:'none',background:'#fde68a',padding:'2px 10px',borderRadius:10}}>
                {p.name} — {Math.ceil((new Date(p.deadline)-new Date())/86400000)}d left
              </a>)}
            </div>
          </div>
        </div>
      )}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
        <div><h1 style={{margin:0,fontSize:28,fontWeight:800,color:'#0f172a'}}>Tasks</h1>
            <p style={{margin:'4px 0 0',color:'#64748b'}}>{tasks.length} total · {tasks.filter(t=>t.status==='in-progress').length} in progress</p></div>
        <button style={S.btn('primary')} onClick={()=>setModal(true)}><FiPlus/> Add Task</button>
      </div>

      {selected.size>0&&(
        <div style={{display:'flex',gap:8,alignItems:'center',background:'#eff6ff',border:'1.5px solid #6366f1',borderRadius:10,padding:'10px 14px',marginBottom:12,flexWrap:'wrap'}}>
          <span style={{fontSize:13,fontWeight:700,color:'var(--pm-primary)'}}>{selected.size} task{selected.size!==1?'s':''} selected</span>
          <span style={{color:'#94a3b8',fontSize:12}}>Move to:</span>
          {['in-progress','review','completed'].map(s=><button key={s} onClick={()=>bulkStatusUpdate(s)} style={{padding:'5px 12px',borderRadius:8,border:'none',background:'var(--pm-primary)',color:'#fff',fontWeight:600,fontSize:11,cursor:'pointer',textTransform:'capitalize'}}>{s}</button>)}
          <button onClick={bulkDelete} style={{padding:'5px 12px',borderRadius:8,border:'none',background:'#fee2e2',color:'#ef4444',fontWeight:600,fontSize:11,cursor:'pointer'}}>Delete</button>
          <button onClick={()=>setSelected(new Set())} style={{padding:'5px 12px',borderRadius:8,border:'none',background:'#f1f5f9',color:'#64748b',fontWeight:600,fontSize:11,cursor:'pointer'}}>✕ Clear</button>
        </div>
      )}
      {/* Toolbar */}
      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:8,background:'#fff',border:'1.5px solid #e2e8f0',borderRadius:10,padding:'8px 14px',flex:1,minWidth:200}}>
          <FiSearch color="#94a3b8"/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tasks..." style={{border:'none',outline:'none',fontSize:14,flex:1}}/>
        </div>
        <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
          {['all','todo','in-progress','review','completed'].map(s=>(
            <button key={s} onClick={()=>setFilter(s)} style={{padding:'7px 14px',borderRadius:8,border:`1.5px solid ${filter===s?'var(--pm-primary)':'#e2e8f0'}`,background:filter===s?'var(--pm-primary)':'#fff',color:filter===s?'#fff':'#475569',fontWeight:600,fontSize:12,cursor:'pointer',textTransform:'capitalize'}}>{s}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:4}}>
          {[['list','☰ List'],['kanban','⬛ Kanban']].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)} style={S.btn(view===v?'ghost-active':'ghost',true)}>{l}</button>
          ))}
        </div>
      </div>

      {/* List View */}
      {view==='list'&&(()=>{
        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const safePage   = Math.min(page, totalPages);
        const start      = (safePage - 1) * PAGE_SIZE;
        const paginated  = filtered.slice(start, start + PAGE_SIZE);

        // Build page numbers to show (always show first, last, and up to 3 around current)
        const pageNums = [];
        for (let i = 1; i <= totalPages; i++) {
          if (i === 1 || i === totalPages || (i >= safePage - 1 && i <= safePage + 1)) pageNums.push(i);
          else if (pageNums[pageNums.length-1] !== '…') pageNums.push('…');
        }

        const allSelected = filtered.length > 0 && filtered.every(t => selected.has(t._id));
        const someSelected = selected.size > 0 && !allSelected;

        return (
          <div>
            {/* Select-all header */}
            {filtered.length > 0 && (
              <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#f8fafc',borderRadius:10,marginBottom:8,border:'1.5px solid #e2e8f0'}}>
                {/* Checkbox */}
                <div
                  onClick={()=>toggleSelectAll(filtered)}
                  style={{
                    width:18,height:18,borderRadius:4,cursor:'pointer',flexShrink:0,
                    border:`2px solid ${allSelected?'var(--pm-primary)':someSelected?'var(--pm-primary)':'#cbd5e1'}`,
                    background:allSelected?'var(--pm-gradient)':someSelected?'#fff':'#fff',
                    display:'flex',alignItems:'center',justifyContent:'center',transition:'all .15s',
                  }}>
                  {allSelected && <span style={{color:'#fff',fontSize:10,fontWeight:800,lineHeight:1}}>✓</span>}
                  {someSelected && <span style={{color:'var(--pm-primary)',fontSize:11,fontWeight:900,lineHeight:1}}>–</span>}
                </div>
                <span
                  onClick={()=>toggleSelectAll(filtered)}
                  style={{fontSize:13,fontWeight:600,color:'#475569',cursor:'pointer',userSelect:'none'}}>
                  {allSelected
                    ? `All ${filtered.length} tasks selected — click to deselect`
                    : someSelected
                      ? `${selected.size} selected — click to select all ${filtered.length}`
                      : `Select all ${filtered.length} task${filtered.length!==1?'s':''}`}
                </span>
                {selected.size > 0 && (
                  <button
                    onClick={()=>setSelected(new Set())}
                    style={{marginLeft:'auto',padding:'3px 10px',borderRadius:6,border:'none',background:'#e2e8f0',color:'#64748b',fontWeight:600,fontSize:12,cursor:'pointer'}}>
                    ✕ Clear
                  </button>
                )}
              </div>
            )}

            {paginated.map(t=><TaskCard key={t._id} t={t}/>)}
            {filtered.length===0&&<div style={{textAlign:'center',padding:60,color:'#94a3b8'}}><FiCheckSquare size={48} style={{marginBottom:16,opacity:.3}}/><p>No tasks found</p></div>}

            {/* Pagination bar */}
            {filtered.length > PAGE_SIZE && (
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:20,padding:'14px 18px',background:'#fff',borderRadius:12,border:'1px solid #f1f5f9',boxShadow:'0 1px 4px #0000000a',flexWrap:'wrap',gap:10}}>
                {/* Showing info */}
                <span style={{fontSize:13,color:'#64748b'}}>
                  Showing <b>{start+1}</b>–<b>{Math.min(start+PAGE_SIZE,filtered.length)}</b> of <b>{filtered.length}</b> tasks
                </span>

                {/* Page buttons */}
                <div style={{display:'flex',alignItems:'center',gap:4}}>
                  {/* Prev */}
                  <button
                    disabled={safePage===1}
                    onClick={()=>setPage(p=>Math.max(1,p-1))}
                    style={{padding:'6px 12px',borderRadius:8,border:'1.5px solid #e2e8f0',background:safePage===1?'#f8fafc':'#fff',color:safePage===1?'#cbd5e1':'#475569',fontWeight:600,fontSize:13,cursor:safePage===1?'not-allowed':'pointer'}}>
                    ‹ Prev
                  </button>

                  {pageNums.map((n,i)=>
                    n === '…'
                      ? <span key={`dots-${i}`} style={{padding:'0 4px',color:'#94a3b8',fontSize:13}}>…</span>
                      : <button key={n}
                          onClick={()=>setPage(n)}
                          style={{width:34,height:34,borderRadius:8,border:`1.5px solid ${safePage===n?'var(--pm-primary)':'#e2e8f0'}`,background:safePage===n?'var(--pm-gradient)':'#fff',color:safePage===n?'#fff':'#475569',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                          {n}
                        </button>
                  )}

                  {/* Next */}
                  <button
                    disabled={safePage===totalPages}
                    onClick={()=>setPage(p=>Math.min(totalPages,p+1))}
                    style={{padding:'6px 12px',borderRadius:8,border:'1.5px solid #e2e8f0',background:safePage===totalPages?'#f8fafc':'#fff',color:safePage===totalPages?'#cbd5e1':'#475569',fontWeight:600,fontSize:13,cursor:safePage===totalPages?'not-allowed':'pointer'}}>
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Kanban View */}
      {view==='kanban'&&(
        <div style={{overflowX:'auto',paddingBottom:8}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,minmax(240px,1fr))',gap:16,minWidth:960}}>
            {KANBAN_COLS.map(col=>{
              const colTasks = filtered.filter(t=>t.status===col);
              const sc = SCOL[col]||SCOL.todo;
              const isDragTarget = dragId && tasks.find(t=>t._id===dragId)?.status !== col;
              return (
                <div key={col}
                  onDragOver={onDragOver}
                  onDrop={e=>onDrop(e,col)}
                  style={{background: isDragTarget ? '#f0fdf4' : '#f8fafc', borderRadius:14, padding:16,
                    border: isDragTarget ? '2px dashed #22c55e' : '2px solid transparent', transition:'all .15s', minHeight:120}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
                    <span style={{background:sc.bg,color:sc.txt,padding:'4px 12px',borderRadius:10,fontSize:12,fontWeight:700,textTransform:'capitalize'}}>{col}</span>
                    <span style={{fontSize:13,fontWeight:700,color:'#64748b'}}>{colTasks.length}</span>
                  </div>
                  {colTasks.map(t=>(
                    <div key={t._id} draggable
                      onDragStart={e=>onDragStart(e,t._id)}
                      onDragEnd={()=>setDragId(null)}
                      style={{opacity: dragId===t._id ? 0.5 : 1, cursor:'grab', transition:'opacity .15s'}}>
                      <TaskCard t={t}/>
                    </div>
                  ))}
                  {colTasks.length===0&&<div style={{textAlign:'center',padding:'20px 0',color:'#cbd5e1',fontSize:13}}>No tasks</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {modal&&(
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div style={S.modal}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:22}}>
              <h3 style={{margin:0,fontSize:20,fontWeight:700}}>New Task</h3>
              <button style={{background:'none',border:'none',cursor:'pointer',fontSize:24}} onClick={()=>setModal(false)}><FiX/></button>
            </div>
            <label style={S.label}>Title *<input style={S.inp} value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Task title"/></label>
            <label style={S.label}>Description<textarea style={{...S.inp,resize:'vertical',minHeight:70}} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3}/></label>
            <div style={{display:'grid',gridTemplateColumns: canAssign ? '1fr 1fr' : '1fr',gap:12}}>
              <label style={S.label}>Project
                <select style={S.inp} value={form.project} onChange={e=>setForm({...form,project:e.target.value,assignedTo:''})}>
                  <option value="">Select project</option>
                  {projects.map(p=><option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </label>
              {canAssign&&<label style={S.label}>Assign To
                {form.project && assignableUsers.length === 0
                  ? <div style={{...S.inp, color:'#94a3b8', fontSize:13, background:'#f8fafc'}}>No team members in this project</div>
                  : <select style={S.inp} value={form.assignedTo} onChange={e=>setForm({...form,assignedTo:e.target.value})}>
                      <option value="">Unassigned</option>
                      {assignableUsers.map(u=><option key={u.id||u._id} value={u.id||u._id}>{u.name} ({u.role})</option>)}
                    </select>
                }
              </label>}
              <label style={S.label}>Priority
                <select style={S.inp} value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>
                  {['low','medium','high','critical'].map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label style={S.label}>Due Date *<input type="date" style={{...S.inp, borderColor: !form.dueDate ? '#fca5a5' : undefined}} required value={form.dueDate} onChange={e=>setForm({...form,dueDate:e.target.value})}/></label>
              {!isClient && <label style={S.label}>Est. Hours<input type="number" style={S.inp} value={form.estimatedHours} onChange={e=>setForm({...form,estimatedHours:e.target.value})} min={0}/></label>}
            </div>
            {!isClient && (
              <div style={{marginTop:12,padding:'12px 14px',background:'#f8fafc',borderRadius:10,border:'1.5px solid #e2e8f0'}}>
                <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',userSelect:'none'}}>
                  <input type="checkbox" checked={form.recurrence.enabled} onChange={e=>setForm({...form,recurrence:{...form.recurrence,enabled:e.target.checked}})} style={{width:16,height:16,accentColor:'var(--pm-primary)'}}/>
                  <span style={{fontWeight:700,fontSize:13,color:'#0f172a'}}>🔁 Recurring Task</span>
                </label>
                {form.recurrence.enabled && (
                  <div style={{marginTop:10,display:'flex',gap:10,alignItems:'center'}}>
                    <label style={{fontSize:12,fontWeight:700,color:'#64748b'}}>Repeats</label>
                    <select style={{...S.inp,width:'auto'}} value={form.recurrence.frequency} onChange={e=>setForm({...form,recurrence:{...form.recurrence,frequency:e.target.value}})}>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                )}
              </div>
            )}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:12}}>
              <button style={S.btn('ghost')} onClick={()=>setModal(false)}>Cancel</button>
              <button style={{...S.btn('primary'),opacity:(!form.title.trim()||!form.dueDate)?0.6:1}} disabled={!form.title.trim()||!form.dueDate} onClick={createTask}><FiPlus/> Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editModal&&(
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setEditModal(false)}>
          <div style={S.modal}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:22}}>
              <h3 style={{margin:0,fontSize:20,fontWeight:700}}>✏️ Edit Task</h3>
              <button style={{background:'none',border:'none',cursor:'pointer',fontSize:24}} onClick={()=>setEditModal(false)}><FiX/></button>
            </div>
            <label style={S.label}>Title *<input style={S.inp} value={editForm.title} onChange={e=>setEditForm({...editForm,title:e.target.value})} placeholder="Task title"/></label>
            <label style={S.label}>Description<textarea style={{...S.inp,resize:'vertical',minHeight:70}} value={editForm.description} onChange={e=>setEditForm({...editForm,description:e.target.value})} rows={3}/></label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <label style={S.label}>Status
                <select style={S.inp} value={editForm.status} onChange={e=>setEditForm({...editForm,status:e.target.value})}>
                  {['todo','in-progress','review','completed','blocked'].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label style={S.label}>Priority
                <select style={S.inp} value={editForm.priority} onChange={e=>setEditForm({...editForm,priority:e.target.value})}>
                  {['low','medium','high','critical'].map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              {canAssign&&<label style={S.label}>Assign To
                <select style={S.inp} value={editForm.assignedTo} onChange={e=>setEditForm({...editForm,assignedTo:e.target.value})}>
                  <option value="">Unassigned</option>
                  {users.filter(u=>u.role!=='client').map(u=><option key={u._id||u.id} value={u._id||u.id}>{u.name} ({u.role})</option>)}
                </select>
              </label>}
              <label style={S.label}>Due Date<input type="date" style={S.inp} value={editForm.dueDate} onChange={e=>setEditForm({...editForm,dueDate:e.target.value})}/></label>
              <label style={S.label}>Est. Hours<input type="number" style={S.inp} value={editForm.estimatedHours} onChange={e=>setEditForm({...editForm,estimatedHours:e.target.value})} min={0}/></label>
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:12}}>
              <button style={S.btn('ghost')} onClick={()=>setEditModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={saveEdit}>💾 Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
